import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useMemo, useRef } from 'react';
import styles from './GridList.module.scss';
import { setupTransitionInTimeline } from './GridList.transitions';
import type { GridListProps, GridListRef } from './GridList.data';
import { useScrollTransition } from 'util/react-transition-component';
import GridListItem from './grid-list-item/GridListItem';
import { useMobileViewport } from '../../../hooks/useMobileViewport';
import sharedVariables from '../../../data/shared-variable/shared-variables.json';

export default function GridList({
  blok,
  ref,
  ...props
}: GridListProps & { ref?: Ref<GridListRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLUListElement | null>;
  const isBelow1280 = useMobileViewport(sharedVariables.deviceState.MIN_1280, false);
  const isBelow1024 = useMobileViewport(sharedVariables.deviceState.MIN_1024, false);
  const isBelow768 = useMobileViewport(sharedVariables.deviceState.MIN_768, false);

  const columns = useMemo(() => {
    if (isBelow768) {
      return 1;
    }
    if (isBelow1024) {
      return 2;
    }
    if (isBelow1280) {
      return 3;
    }
    return 4;
  }, [isBelow768, isBelow1024, isBelow1280]);

  useScrollTransition({
    scroll: {
      trigger: elementRef,
      vars: {},
    },
    setupOptions: () => ({
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
    }),
  });

  return (
    <ul
      ref={elementRef}
      className={classNames(styles.gridList, props.className, 'grid-gutter')}
      {...sbEditable(blok)}
    >
      {blok.list.map((item, index) => (
        <GridListItem
          key={item._uid}
          blok={item}
          transitionInDelay={(index % columns) * 0.15}
          className={classNames(styles.item)}
        />
      ))}
    </ul>
  );
}
